import React, { useState, useEffect } from 'react';

import BonusCloseIcon from '@/shared/assets/bonus-close-icon.svg';
import { Box } from '@/shared/ui/box/Box';
import { CloseIconBox, BannerTitleBox, ActivateButtonBox } from './styles';
import { resolveMediaPath } from '@/shared/libs/resolve-media-path';
import { getThemeBrand } from '@/shared/hooks/useBrandTheme';
import { brandIsPh } from '@/shared/config/brand-by-target.mjs';
import { Button } from '@/shared/ui/form/button/button';
import { Condition } from '@/shared/ui/condition/condition';
import { BonusActions } from '@/flat-pages/bonuses/ui/bonus-actions';
import { BonusGamesAndProviders } from '@/flat-pages/bonuses/ui/bonus-games-providers';

import {
  BodyTextBoldPrimary,
  BodyTextBoldSecondary,
} from '@/shared/ui/typography';
import {
  HeadingBoldH5,
  HeadingMediumH5,
  BodyTextRegularB,
} from '@/shared/ui/typography2';

import {
  StyledAlertDialogOverlay,
  StyledAlertDialogContent,
} from '@/shared/ui/alert-dialog/styles';

import { WalletButton, WrapperButton } from '@/shared/ui/wallet-button/styles';
import { OptionsBlock } from './components/options-block';
import { ConditionBlock } from './components/condition-block';

import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import * as Dialog from '@radix-ui/react-dialog';
import { useRouter } from 'next/navigation';
import { useScopedI18n, useCurrentLocale } from '@locales/client';
import { useLocaleFormat } from '@/shared/hooks/useLocaleFormat';
import {
  useTransactionBonusID,
  useTransactionStatus,
  useBonusInfoProvider,
  useBonusInfoGames,
  useIsBonusActive,
} from '@/shared/providers/notifications-provider/store';

import {
  BonusDetails,
  BonusDialogContent,
  BonusImage,
  BonusItem,
} from '../../styles';
import { createDialogCloseOnlyHandler } from '@/shared/libs/dialog-open-change';
import type { BonusListItem } from '@/flat-pages/bonuses';

interface BonusViewModalProps {
  bonus: BonusListItem | null;
  onClose: () => void;
  onActivate: (
    bonus: BonusListItem,
    conditionActive: number | null
  ) => Promise<boolean | undefined>;
  burnOpenClickHandler: (bonus: BonusListItem) => void;
  updateDate: () => Promise<void>;
}

export const BonusViewModal = ({
  bonus,
  onClose,
  onActivate,
  burnOpenClickHandler,
  updateDate,
}: BonusViewModalProps) => {
  const [conditionActive, setConditionActive] = useState<number | null>(null);
  const [timeLoading, setTimeLoading] = useState(true);
  const [activationBlocked, setActivationBlocked] = useState(false);
  const t = useScopedI18n('bonus');
  const tCommon = useScopedI18n('common');
  const locale = useCurrentLocale();
  const router = useRouter();
  const formatUrl = useLocaleFormat();

  const transactionBonusID = useTransactionBonusID();
  const transactionStatus = useTransactionStatus();
  const isTransactionPending = Boolean(
    transactionBonusID && transactionStatus === 'pending'
  );
  const bonusInfoProvider = useBonusInfoProvider();
  const bonusInfoGames = useBonusInfoGames();
  const isBonusActive = useIsBonusActive();

  useEffect(() => {
    const bonusState = bonus?.state;
    const shouldSetTimer =
      bonusState !== 'available' && bonusState !== 'active';

    if (shouldSetTimer) {
      const timer = setTimeout(() => {
        setTimeLoading(false);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [bonus?.state]);

  useEffect(() => {
    if (conditionActive === null && bonus?.options?.length) {
      setConditionActive(brandIsPh ? bonus.options.length - 1 : 0);
    }
    return () => {
      setConditionActive(null);
    };
  }, [bonus]);

  useEffect(() => {
    setActivationBlocked(false);
  }, [bonus?.id]);

  const handleActivateClick = async () => {
    if (!bonus) return;
    const result = await onActivate(bonus, conditionActive);
    if (result === false) {
      setActivationBlocked(true);
    }
  };

  if (!bonus) return null;

  return (
    <Dialog.Root
      open={Boolean(bonus?.id)}
      onOpenChange={createDialogCloseOnlyHandler(onClose)}
    >
      <Dialog.Portal>
        <StyledAlertDialogOverlay onClick={onClose} />
        <StyledAlertDialogContent
          aria-describedby=""
          data-item={`bonus_view_${bonus?.id || bonus?.name || 'unknown'}`}
        >
          <BonusDialogContent $modBonus={true}>
            <VisuallyHidden>
              <Dialog.Title>bonus view</Dialog.Title>
            </VisuallyHidden>
            <BonusItem $isActive={bonus?.state === 'active'}>
              <BonusImage
                $dialogMode={true}
                $imageUrl={resolveMediaPath(
                  bonus?.pic || getThemeBrand().assets.loader.default
                )}
              >
                <CloseIconBox>
                  <BonusCloseIcon onClick={onClose} />
                </CloseIconBox>
                <BannerTitleBox>
                  <HeadingBoldH5>{bonus?.name}</HeadingBoldH5>
                </BannerTitleBox>
              </BonusImage>

              <BonusDetails>
                <OptionsBlock
                  options={bonus?.options}
                  changeConditionActive={setConditionActive}
                  conditionActive={conditionActive}
                  t={t}
                />
                <ConditionBlock
                  options={bonus?.options}
                  conditionActive={conditionActive}
                  t={t}
                />

                <BonusGamesAndProviders
                  bonus={bonus}
                  tCommon={tCommon}
                  t={t}
                  router={router}
                  locale={locale}
                />

                {/* <DescriptionWrapper> */}
                <HeadingMediumH5>{t('card.modal_desc')}</HeadingMediumH5>

                <BodyTextRegularB
                  color="#fff"
                  dangerouslySetInnerHTML={{
                    __html: bonus?.description,
                  }}
                />

                {/* </DescriptionWrapper> */}

                <Condition
                  match={
                    bonus?.state !== 'available' && bonus?.state !== 'active'
                  }
                >
                  <WrapperButton className="customBtn">
                    <WalletButton
                      style={{ flexGrow: '1' }}
                      $isLoading={timeLoading}
                      variant="gray"
                      disabled={true}
                    >
                      <BonusActions
                        bonus={bonus}
                        updateDate={updateDate}
                        t={t}
                        locale={locale}
                      />
                    </WalletButton>
                  </WrapperButton>
                </Condition>

                <Condition match={bonus?.state == 'available'}>
                  <ActivateButtonBox>
                    <Button
                      fill
                      disabled={activationBlocked || isTransactionPending}
                      variant={
                        isTransactionPending ? 'danger' : 'orangeGradient'
                      }
                      onClick={handleActivateClick}
                      isLoading={isTransactionPending}
                      data-analytics-id={`bonus_view_activate_${bonus?.id || 'unknown'}`}
                    >
                      <Condition match={!isTransactionPending}>
                        <BodyTextBoldSecondary>
                          {t('card.button_active')}
                        </BodyTextBoldSecondary>
                      </Condition>
                    </Button>
                  </ActivateButtonBox>
                </Condition>

                <Condition match={bonus?.state === 'active'}>
                  <ActivateButtonBox>
                    <Button
                      style={{ flexGrow: '1' }}
                      fill
                      variant="danger"
                      disabled={isTransactionPending}
                      onClick={() => {
                        burnOpenClickHandler(bonus);
                        onClose();
                      }}
                    >
                      <Box>
                        <BodyTextBoldSecondary>
                          {t('burn_modal.burn')}
                        </BodyTextBoldSecondary>
                      </Box>
                    </Button>
                    <Button
                      style={{ flexGrow: '2' }}
                      fill
                      variant="orangeGradient"
                      disabled={isTransactionPending}
                      onClick={() => {
                        if (
                          !bonusInfoProvider &&
                          !bonusInfoGames &&
                          isBonusActive
                        ) {
                          router.push(formatUrl('/'));
                        } else {
                          router.push(formatUrl('/games?filter=bonus'));
                        }
                      }}
                    >
                      <Box>
                        <BodyTextBoldPrimary>
                          {t('card.to_games')}
                        </BodyTextBoldPrimary>
                      </Box>
                    </Button>
                  </ActivateButtonBox>
                </Condition>
              </BonusDetails>
            </BonusItem>
          </BonusDialogContent>
        </StyledAlertDialogContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
